import { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Brand colors
const BLUE = '#0504AA';
const BLUE_MID = '#2322C4';
const AMBER = '#E8A731';


const NODE_COUNT = 80;
const CONNECTION_DIST = 4;

interface NodeData {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  basePosition: THREE.Vector3;
  color: THREE.Color;
}

function generateNodes(): NodeData[] {
  const nodes: NodeData[] = [];
  for (let i = 0; i < NODE_COUNT; i++) {
    const x = (Math.random() - 0.5) * 22;
    const y = (Math.random() - 0.5) * 14;
    const z = (Math.random() - 0.5) * 10;
    const pos = new THREE.Vector3(x, y, z);
    const isAmber = Math.random() < 0.3;
    nodes.push({
      position: pos.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 0.003,
        (Math.random() - 0.5) * 0.003,
        (Math.random() - 0.5) * 0.003
      ),
      basePosition: pos.clone(),
      color: new THREE.Color(isAmber ? AMBER : (Math.random() < 0.5 ? BLUE : BLUE_MID)),
    });
  }
  return nodes;
}

function NeuralScene() {
  const groupRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const linesRef = useRef<THREE.LineSegments>(null);
  const { pointer, viewport } = useThree();
  
  const nodes = useMemo(() => generateNodes(), []);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  
  
  // Pre-allocate color array for instanced mesh
  const colorArray = useMemo(() => {
    const arr = new Float32Array(NODE_COUNT * 3);
    nodes.forEach((n, i) => {
      arr[i * 3] = n.color.r;
      arr[i * 3 + 1] = n.color.g;
      arr[i * 3 + 2] = n.color.b;
    });
    return arr;
  }, [nodes]);

  useFrame((state, delta) => {
    if (!meshRef.current || !groupRef.current) return;

    const time = state.clock.elapsedTime;
    
    // Slow auto-rotation
    groupRef.current.rotation.y += delta * 0.04;

    // Mouse world position (approximate)
    const mouseX = pointer.x * viewport.width * 0.5;
    const mouseY = pointer.y * viewport.height * 0.5;

    // Update nodes
    for (let i = 0; i < NODE_COUNT; i++) {
      const node = nodes[i];
      
      // Gentle drift
      node.position.add(node.velocity);
      
      // Spring back to base
      const dx = node.basePosition.x - node.position.x;
      const dy = node.basePosition.y - node.position.y;
      const dz = node.basePosition.z - node.position.z;
      node.velocity.x += dx * 0.001;
      node.velocity.y += dy * 0.001;
      node.velocity.z += dz * 0.001;
      
      // Mouse repulsion
      const mdx = node.position.x - mouseX;
      const mdy = node.position.y - mouseY;
      const mDist = Math.sqrt(mdx * mdx + mdy * mdy);
      if (mDist < 3) {
        const force = (3 - mDist) * 0.008;
        node.velocity.x += (mdx / mDist) * force;
        node.velocity.y += (mdy / mDist) * force;
      }
      
      // Damping
      node.velocity.multiplyScalar(0.97);
      
      // Gentle floating
      const floatOffset = Math.sin(time * 0.5 + i * 0.7) * 0.08;
      
      dummy.position.set(
        node.position.x,
        node.position.y + floatOffset,
        node.position.z
      );
      
      const scale = 0.06 + Math.sin(time * 1.2 + i * 0.5) * 0.02;
      dummy.scale.setScalar(scale);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;

    // Update connections
    if (linesRef.current) {
      const positions: number[] = [];
      for (let i = 0; i < NODE_COUNT; i++) {
        for (let j = i + 1; j < NODE_COUNT; j++) {
          const dist = nodes[i].position.distanceTo(nodes[j].position);
          if (dist < CONNECTION_DIST) {
            positions.push(
              nodes[i].position.x, nodes[i].position.y, nodes[i].position.z,
              nodes[j].position.x, nodes[j].position.y, nodes[j].position.z
            );
          }
        }
      }
      const geom = linesRef.current.geometry as THREE.BufferGeometry;
      geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geom.attributes.position.needsUpdate = true;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Nodes */}
      <instancedMesh ref={meshRef} args={[undefined, undefined, NODE_COUNT]}>
        <sphereGeometry args={[1, 12, 12]}>
          <instancedBufferAttribute
            attach="attributes-color"
            args={[colorArray, 3]}
          />
        </sphereGeometry>
        <meshStandardMaterial
          vertexColors
          emissive={new THREE.Color(BLUE_MID)}
          emissiveIntensity={0.6}
          roughness={0.3}
          metalness={0.6}
        />
      </instancedMesh>

      {/* Connections */}
      <lineSegments ref={linesRef}>
        <bufferGeometry />
        <lineBasicMaterial color={AMBER} transparent opacity={0.3} />
      </lineSegments>

      {/* Ambient */}
      <ambientLight intensity={0.8} color="#FEFEFE" />
      
      {/* Point lights */}
      <pointLight position={[5, 3, 2]} intensity={1.5} color={AMBER} distance={15} />
      <pointLight position={[-6, -2, 3]} intensity={1} color={BLUE_MID} distance={12} />
      <pointLight position={[0, 5, -4]} intensity={0.8} color={AMBER} distance={10} />
    </group>
  );
}

function NeuralNetwork3D() {
  const [canRender3D, setCanRender3D] = useState(true);

  useEffect(() => {
    // Mobile fallback: skip 3D on low-end devices
    if (typeof navigator !== 'undefined' && navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
      setCanRender3D(false);
    }
  }, []);

  if (!canRender3D) {
    return (
      <div
        id="neural-canvas"
        style={{
          background: `radial-gradient(ellipse 60% 60% at 50% 40%, ${BLUE}33 0%, #FEFEFE 100%)`,
        }}
      />
    );
  }

  return (
    <Canvas
      id="neural-canvas"
      camera={{ position: [0, 0, 12], fov: 55 }}
      gl={{ antialias: true, alpha: false }}
      onCreated={({ gl }) => {
        gl.setClearColor(new THREE.Color('#FEFEFE'));
      }}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0 }}
    >
      <NeuralScene />
    </Canvas>
  );
}

export default NeuralNetwork3D;
